import { useEffect, useState } from 'react';
import { apiFetch } from './apiFetch';
import { getToken } from './PasswordGate';
import { emailToPerson } from '../config/personNames';

// Resolves both household members' { person key → numeric user_id } in one
// place so the split UI, scope toggle, and any other per-person feature can
// rely on a single source of truth. Combines /api/auth/me (caller) with
// /api/family (other member) into a stable {dakota, natalia} map.
//
// Returns null IDs when a member can't be resolved (e.g. demo mode, or the
// caller's email doesn't match either known person). Callers that need to
// gate UI on "both IDs known" should check for both being non-null.

export interface HouseholdIds {
  dakota: number | null;
  natalia: number | null;
}

export type HouseholdState =
  | { status: 'loading'; ids: HouseholdIds }
  | { status: 'ready'; ids: HouseholdIds; selfId: number | null }
  | { status: 'error'; ids: HouseholdIds; error: string };

const EMPTY_IDS: HouseholdIds = { dakota: null, natalia: null };

export function useHousehold(): HouseholdState {
  const [state, setState] = useState<HouseholdState>({
    status: 'loading',
    ids: EMPTY_IDS,
  });

  useEffect(() => {
    let cancelled = false;
    const token = getToken();
    if (!token) {
      setState({ status: 'ready', ids: EMPTY_IDS, selfId: null });
      return;
    }
    Promise.all([apiFetch('/api/auth/me'), apiFetch('/api/family')])
      .then(async ([meRes, famRes]) => {
        if (!meRes.ok) throw new Error(`me ${meRes.status}`);
        if (!famRes.ok) throw new Error(`family ${famRes.status}`);
        const me = (await meRes.json()) as {
          mode: 'real' | 'demo' | 'real-legacy';
          user?: { id: number; email: string };
        };
        const fam = (await famRes.json()) as {
          members: { id: number; email: string }[];
        };
        if (me.mode !== 'real' || !me.user) {
          // Demo / legacy auth doesn't have a real identity — return empties.
          setState({ status: 'ready', ids: EMPTY_IDS, selfId: null });
          return;
        }
        const ids: HouseholdIds = { dakota: null, natalia: null };
        const selfPerson = emailToPerson(me.user.email);
        if (selfPerson) ids[selfPerson] = me.user.id;
        for (const m of fam.members) {
          const p = emailToPerson(m.email);
          if (p) ids[p] = m.id;
        }
        if (cancelled) return;
        setState({ status: 'ready', ids, selfId: me.user.id });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState({ status: 'error', ids: EMPTY_IDS, error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
